import { faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import Collapsible from 'src/components/0100_collapsible';
import Input from 'src/components/0100_input';
import Loading from 'src/components/0100_loading';
import { updateModSection } from 'src/graphql/mutations/events.graphql';
import {
  IUpdateModSectionMutation,
  IUpdateModSectionMutationVariables,
} from 'src/graphql/mutations/events.graphql.types';
import useRootUser from 'src/hooks/players/useRootUser';
import { useMutation } from 'urql';
import Mod, { TMod } from './Mod';

interface IProps {
  id: number;
  canManageMod?: boolean;
  name: string;
  signupLimit: number;
  eventAllowsMoreSignup: boolean;
  waitlistAllowed: boolean;
  eventHasEnded: boolean;
  modSignupIsOpen: boolean;
  mods: TMod[];
}

const Section: FC<IProps> = ({
  id,
  canManageMod,
  eventAllowsMoreSignup,
  waitlistAllowed,
  eventHasEnded,
  modSignupIsOpen,
  ...props
}) => {
  const {
    reset,
    register,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: { name: props.name, signupLimit: props.signupLimit },
  });
  const [isExpanded, setIsExpanded] = useState(true);
  const [isUpdateSuccessful, setIsUpdateSuccessful] = useState(false);
  const { name, signupLimit } = watch();
  const { rootUserId } = useRootUser();
  const thisSectionSignups = useMemo(
    () =>
      props.mods.filter(mod =>
        mod.modSignups?.some(x => x.eventAttendee.user.id === rootUserId),
      ).length,
    [props.mods, rootUserId],
  );

  const [mutationResult, mutate] = useMutation<
    IUpdateModSectionMutation,
    IUpdateModSectionMutationVariables
  >(updateModSection);

  const handleChange = useCallback(() => {
    if (!isDirty) return;

    mutate({
      modSectionId: id,
      signupLimit: Number(signupLimit),
      name,
    }).then(res => {
      if (res.data?.updateModSection?.modSection?.id) {
        setIsUpdateSuccessful(true);
        setTimeout(() => setIsUpdateSuccessful(false), 3000);
      }
    });
  }, [mutate, name, signupLimit, id, isDirty]);

  useEffect(() => {
    reset({
      name: props.name,
      signupLimit: props.signupLimit,
    });
  }, [reset, props.name, props.signupLimit]);

  useEffect(() => {
    if (isDirty) {
      setIsUpdateSuccessful(false);
    }
  }, [isDirty]);

  return (
    <div className="grid gap-2 gray-box border-t border-juno-gray-700">
      <div
        className={clsx(
          'grid grid-cols-1 lg:grid-cols-2 gap-2 transition-all duration-300 ease-in-out p-2 px-0 md:px-2',
          isUpdateSuccessful ? 'bg-lime-600/75' : 'bg-transparent',
        )}
      >
        <div className="flex justify-start w-full">
          {canManageMod ? (
            <Input fullWidth {...register('name', { onBlur: handleChange })} />
          ) : (
            <div className="text-shadow text-xl">{name}</div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          {canManageMod ? (
            <div className="flex justify-end items-center gap-2">
              {mutationResult.fetching && <Loading size="small" />}
              <div>Signup Limit</div>
              <div className="w-12">
                <Input
                  fullWidth
                  className="text-right"
                  type="number"
                  {...register('signupLimit', { onBlur: handleChange })}
                  onFocus={x => x.target.select()}
                />
              </div>
            </div>
          ) : (
            <div className="text-right text-shadow text-xl">
              {thisSectionSignups}/{signupLimit} Signups
            </div>
          )}
          <FontAwesomeIcon
            icon={faChevronUp}
            className={clsx(
              !isExpanded && '-scale-y-100 cursor transition-all duration-300',
            )}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        </div>
      </div>
      <Collapsible isExpanded={isExpanded}>
        {canManageMod ||
        props.mods.filter(x => x.participationLimit).length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 p-2 px-0 md:px-2">
            {props.mods.map(mod => (
              <Mod
                key={mod.id}
                eventAllowsMoreSignup={eventAllowsMoreSignup}
                sectionAllowsMoreSignup={thisSectionSignups < signupLimit}
                waitlistAllowed={waitlistAllowed}
                eventHasEnded={eventHasEnded}
                modSignupIsOpen={modSignupIsOpen}
                canManageMod={canManageMod}
                {...mod}
              />
            ))}
            {canManageMod && (
              <Mod canManageMod={canManageMod} modSectionId={id} />
            )}
          </div>
        ) : (
          <div className="p-2 pt-0 px-0 md:px-2">
            No Mods available yet. Please check back later
          </div>
        )}
      </Collapsible>
    </div>
  );
};

export default Section;
